/**
 * Hook per leggere l'utilizzo corrente dell'org rispetto ai limiti del piano.
 *
 * Conta: customers, estimates totali, stratigrafie custom totali.
 * Confronta con i limiti di `PLAN_RULES[plan]` (vedi `useOrgPlan.ts`).
 *
 * Usato per:
 *  - disabilitare bottoni "Nuovo X" quando l'org ha raggiunto il limite (Solo)
 *  - mostrare contatori "X/15" inline + upsell card
 *
 * Una sola query parallela `head: true + count: 'exact'` per non scaricare
 * record. staleTime 30s come `useSidebarCounts`.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { usePlanLimit, isUnlimited, type LimitKey } from '@/hooks/useOrgPlan';

interface EntityCounts {
  customers: number;
  estimates: number;
  stratigraphies: number;
}

interface LimitInfo {
  /** Conteggio corrente per l'org. */
  current: number;
  /** Tetto del piano. -1 = illimitato. */
  max: number;
  /** True se max è illimitato (-1) — l'utente non vedrà mai blocchi. */
  unlimited: boolean;
  /** True se current >= max (e non unlimited). */
  atLimit: boolean;
  /** Etichetta "X/15" o "X" se illimitato. */
  label: string;
}

interface UsePlanUsageResult {
  customers: LimitInfo;
  estimates: LimitInfo;
  stratigraphies: LimitInfo;
  isLoading: boolean;
}

function buildLimitInfo(current: number, max: number): LimitInfo {
  const unlimited = isUnlimited(max);
  return {
    current,
    max,
    unlimited,
    atLimit: !unlimited && current >= max,
    label: unlimited ? String(current) : `${current}/${max}`,
  };
}

export function usePlanUsage(): UsePlanUsageResult {
  const { currentOrganizationId } = useCurrentOrganization();
  const maxCustomers = usePlanLimit('maxCustomers' as LimitKey);
  const maxEstimates = usePlanLimit('maxEstimates' as LimitKey);
  const maxStratigraphies = usePlanLimit('maxStratigraphies' as LimitKey);

  const { data, isLoading } = useQuery<EntityCounts>({
    queryKey: ['plan-usage', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<EntityCounts> => {
      if (!currentOrganizationId) {
        return { customers: 0, estimates: 0, stratigraphies: 0 };
      }
      const [custRes, estRes, stratRes] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('estimates')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId),
        // Solo stratigrafie custom (non quelle "certified" condivise). Le custom
        // sono quelle create dall'utente: filtro su organization_id.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('stratigraphies')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId),
      ]);
      return {
        customers: custRes.count ?? 0,
        estimates: estRes.count ?? 0,
        stratigraphies: stratRes.count ?? 0,
      };
    },
  });

  const counts = data ?? { customers: 0, estimates: 0, stratigraphies: 0 };

  return {
    customers: buildLimitInfo(counts.customers, maxCustomers),
    estimates: buildLimitInfo(counts.estimates, maxEstimates),
    stratigraphies: buildLimitInfo(counts.stratigraphies, maxStratigraphies),
    isLoading,
  };
}
